import React, { useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './style.css';

const BODY_CLASS_LINK_POPUP_OPEN = 'ql-link-tooltip-open';

function useLinkPopupBodyClass(editorRef) {
  useEffect(() => {
    let cancelled = false;
    let cleanup = null;

    const run = () => {
      const wrapper = editorRef.current;
      if (!wrapper || cancelled) return;

      const container = wrapper.querySelector('.ql-container');
      const tooltip = container?.querySelector('.ql-tooltip');
      if (!tooltip) return;

      const observer = new MutationObserver(() => {
        if (tooltip.classList.contains('ql-hidden')) {
          document.body.classList.remove(BODY_CLASS_LINK_POPUP_OPEN);
        } else {
          document.body.classList.add(BODY_CLASS_LINK_POPUP_OPEN);
        }
      });

      observer.observe(tooltip, {
        attributes: true,
        attributeFilter: ['class']
      });

      const handleKeyDown = (e) => {
        if (e.key !== 'Escape') return;
        if (tooltip.classList.contains('ql-hidden')) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        tooltip.classList.add('ql-hidden');
        document.body.classList.remove(BODY_CLASS_LINK_POPUP_OPEN);
      };

      // Capture phase on document so we run before MUI Modal's Escape handler (which is on the modal div and closes the drawer)
      document.addEventListener('keydown', handleKeyDown, true);

      cleanup = () => {
        observer.disconnect();
        document.removeEventListener('keydown', handleKeyDown, true);
        document.body.classList.remove(BODY_CLASS_LINK_POPUP_OPEN);
      };
    };

    run();
    if (!cleanup) {
      const id = requestAnimationFrame(() => {
        if (!cancelled) run();
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id);
        if (typeof cleanup === 'function') cleanup();
      };
    }

    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') cleanup();
    };
  }, []);
}

const Editor = ({ value, onChange, variant = 'minimal', modules = {} }) => {
  const editorRef = useRef(null);
  useLinkPopupBodyClass(editorRef);

  const defaultModules = {
    toolbar: [['bold', 'italic', 'underline'], ['link']]
  };

  const mergedModules = {
    ...defaultModules,
    ...modules
  };

  return (
    <div ref={editorRef}>
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={mergedModules}
      />
    </div>
  );
};

export default Editor;
